import React, { useState } from 'react';
import styles from './BlueprintCard.module.css';
import BlueprintModal from './BlueprintModal';

/**
 * Task Plan Icon - Simple document/checklist icon
 */
const TaskPlanIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    className={className}
    width="16" 
    height="16" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M9 12H15M9 16H12" 
      stroke="currentColor" 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Interface for the task_summary field inside the Blueprint JSON
 */
interface TaskSummary {
  goal?: string;
  design?: string[];
  features?: string[];
  implementation?: string[];
}

/**
 * Interface for the summary_card field (new compact plan card model).
 * This is intentionally small and predictable so the chat card never "explodes".
 */
interface SummaryCard {
  goal?: string; // 1 sentence
  in_scope?: string; // 1 sentence (same as feature)
  pages_touched?: string[]; // max 5
  assumptions?: string[]; // max 2
  out_of_scope?: string[]; // max 3
  acceptance_check?: string; // 1 line
}

/**
 * Interface for Blueprint JSON data
 */
interface BlueprintData {
  blueprint_title?: string;
  version?: string;
  summary_card?: SummaryCard;
  task_summary?: TaskSummary;
  [key: string]: any;
}

interface BlueprintCardProps {
  data: BlueprintData;
  isTyping?: boolean;
}

/**
 * BlueprintCard Component
 * 
 * Displays the task_summary from the Blueprint JSON in a simple card.
 * Mobile-friendly with responsive design.
 * Includes a link to open the full detailed plan modal.
 */
const BlueprintCard: React.FC<BlueprintCardProps> = ({ data, isTyping = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const version = data.version || '1.0';
  const summaryCard = data.summary_card;
  const legacySummary = data.task_summary;

  // Show loading state while streaming
  if (isTyping) {
    return (
      <div className={styles.card}>
        <div className={styles.header}>
          <TaskPlanIcon className={styles.headerIcon} />
          <span className={styles.headerTitle}>TASK PLAN</span>
        </div>
        <div className={styles.loading}>Generating plan...</div>
      </div>
    );
  }

  // If no summary_card and no legacy summary, just show link to full plan
  if (!summaryCard && !legacySummary) {
    return (
      <>
        <button className={styles.link} onClick={() => setIsModalOpen(true)}>
          View detailed Plan → v{version}
        </button>
        <BlueprintModal data={data} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </>
    );
  }

  /**
   * Prefer the new compact summary_card when available.
   * Fallback to legacy task_summary for older plans already stored in chat history.
   */
  if (summaryCard) {
    const pages = (summaryCard.pages_touched || []).slice(0, 5);
    const assumptions = (summaryCard.assumptions || []).slice(0, 2);
    const outOfScope = (summaryCard.out_of_scope || []).slice(0, 3);

    return (
      <>
        <div className={styles.card}>
          {/* Card Header */}
          <div className={styles.header}>
            <TaskPlanIcon className={styles.headerIcon} />
            <span className={styles.headerTitle}>TASK PLAN</span>
          </div>

          {/* Content (compact) */}
          <div className={styles.content}>
            {summaryCard.goal && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>Goal</div>
                <div className={`${styles.text} ${styles.clamp2}`}>{summaryCard.goal}</div>
              </div>
            )}

            {summaryCard.in_scope && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>In scope</div>
                <div className={`${styles.text} ${styles.clamp2}`}>{summaryCard.in_scope}</div>
              </div>
            )}

            {pages.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>Pages touched</div>
                <div className={styles.chipsRow}>
                  {pages.map((p, i) => (
                    <span key={`${p}-${i}`} className={styles.chip}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {assumptions.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>Assumptions</div>
                <ul className={styles.list}>
                  {assumptions.map((item, i) => (
                    <li key={i} className={styles.listItem}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {outOfScope.length > 0 && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>Out of scope</div>
                <ul className={styles.list}>
                  {outOfScope.map((item, i) => (
                    <li key={i} className={styles.listItem}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {summaryCard.acceptance_check && (
              <div className={styles.section}>
                <div className={styles.sectionTitle}>Acceptance</div>
                <div className={`${styles.text} ${styles.clamp2}`}>{summaryCard.acceptance_check}</div>
              </div>
            )}
          </div>

          {/* Footer with link to full plan */}
          <div className={styles.footer}>
            <button className={styles.footerLink} onClick={() => setIsModalOpen(true)}>
              View details → v{version}
            </button>
          </div>
        </div>

        <BlueprintModal data={data} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </>
    );
  }

  // Legacy rendering (task_summary) - keeps backward compatibility.
  // Guard: if legacySummary is undefined, return null (nothing to render).
  if (!legacySummary) {
    return null;
  }
  const summary = legacySummary;

  return (
    <>
      <div className={styles.card}>
        {/* Card Header */}
        <div className={styles.header}>
          <TaskPlanIcon className={styles.headerIcon} />
          <span className={styles.headerTitle}>TASK PLAN</span>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* Goal Section */}
          {summary.goal && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Goal</div>
              <div className={styles.text}>{summary.goal}</div>
            </div>
          )}

          {/* Design Section */}
          {summary.design && summary.design.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Design</div>
              <ul className={styles.list}>
                {summary.design.map((item, i) => (
                  <li key={i} className={styles.listItem}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Features Section */}
          {summary.features && summary.features.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Features</div>
              <ul className={styles.list}>
                {summary.features.map((item, i) => (
                  <li key={i} className={styles.listItem}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Implementation Section */}
          {summary.implementation && summary.implementation.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Implementation</div>
              <ul className={styles.list}>
                {summary.implementation.map((item, i) => (
                  <li key={i} className={styles.listItem}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer with link to full plan */}
        <div className={styles.footer}>
          <button className={styles.footerLink} onClick={() => setIsModalOpen(true)}>
            View detailed Plan → v{version}
          </button>
        </div>
      </div>

      {/* Full plan modal */}
      <BlueprintModal data={data} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default BlueprintCard;
